/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Sync } from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useDeleteUser, useListPeople } from "../../api/users";
import RedirectLink from "../../components/RedirectLink";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import {
  dataGridButtonStyles,
  iamDividerStyles,
} from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import utilColors from "../../styles/utilColors";
import { adaptUsers } from "../../adapters/users";
import TimeCell from "../../components/TimeCell";
import DataTable from "../../components/DataTable";
import useIamModal from "../IamModal/UseIamModal";
import { useAddUserDialog, useUpdateManagerDialog } from "./dialog/DialogHooks";
import { useHasPermission } from "../../api/iam";

// Column layout so the grid takes every pixel left under the header row,
// matching the control planes / clusters pages.
const styles = {
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

const People = ({ displayNotification, setConfirmationDialog }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const { IamModal, IamButton, canViewIamModal } = useIamModal(
    "/user-management/users"
  );

  const [people, setPeople] = useState([]);

  const {
    postData: listPeople,
    response: listPeopleResp,
    isLoading: isLoadingPrs,
  } = useListPeople();

  const {
    postData: deleteUser,
    response: deleteUserResp,
    responseStatus: deleteUserStatus,
  } = useDeleteUser();

  const { addUserDialog, setOpenAddUserDialog } = useAddUserDialog(
    people,
    listPeople,
    displayNotification
  );
  const { updateManagerDialog, setOpenUpdateManagerDialog } =
    useUpdateManagerDialog(people, listPeople, displayNotification);

  useEffect(() => {
    listPeople();
  }, []);

  useEffect(() => {
    if (Array.isArray(listPeopleResp?.users)) {
      setPeople(adaptUsers(listPeopleResp.users));
    }
  }, [listPeopleResp]);

  useEffect(() => {
    displayNotification(deleteUserResp, deleteUserStatus, () => {
      listPeople();
    });
  }, [deleteUserResp, deleteUserStatus]);

  const handleDelete = (user) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete User",
      content: `You are going to delete the user: "${user?.emailId}"`,
      handleDialogConfirm: () => {
        deleteUser(`/${user?.id}`);
      },
    });
  };

  const columns = [
    {
      field: "name",
      renderHeader: () => "Name",
      flex: 2,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { name } }) => name || "",
    },
    {
      field: "githubId",
      flex: 1.5,
      renderHeader: () => "Github",
      renderCell: ({ row: { githubId } }) =>
        githubId ? (
          <RedirectLink
            href={`https://github.com/${githubId}`}
            color={utilColors(colors).skyBlue}
          >
            {githubId}
          </RedirectLink>
        ) : (
          ""
        ),
    },
    {
      field: "emailId",
      flex: 2.5,
      renderHeader: () => "Email",
      renderCell: ({ row: { emailId } }) => emailId ?? "",
    },
    {
      field: "managerEmailId",
      flex: 2.5,
      renderHeader: () => "Manager",
      renderCell: ({ row: { managerEmailId } }) => managerEmailId ?? "",
    },
    {
      field: "createdOn",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Created On",
      renderCell: ({ row: { createdOn } }) => <TimeCell value={createdOn} />,
    },
    {
      field: "actions",
      type: "actions",
      width: 30,
      getActions: ({ row }) =>
        [
          <GridActionsCellItem
            label="Delete"
            onClick={() => handleDelete(row)}
            showInMenu
          />,
          <GridActionsCellItem
            label="Update Manager"
            onClick={() => {
              setOpenUpdateManagerDialog(row);
            }}
            showInMenu
          />,
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      {IamModal}
      {addUserDialog}
      {updateManagerDialog}
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid item xs={1}>
          <Header
            title="People"
            subtitle="list of people registered in convoy"
          />
        </Grid>
        <Grid item xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            {IamButton}
            {canViewIamModal ? <Box sx={iamDividerStyles(colors)} /> : null}
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => listPeople()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
            {useHasPermission("/user-management/users", "POST", "ADD") ? (
              <DataGridButton
                sx={dataGridButtonStyles.pageTopButtons}
                onClick={setOpenAddUserDialog}
              >
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  ADD
                </Typography>
              </DataGridButton>
            ) : (
              <></>
            )}
          </Box>
        </Grid>
      </Grid>
      <DataTable
        hideFooter
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.id}
        loading={isLoadingPrs}
        rows={people}
        columns={columns}
      />
    </Box>
  );
};

export default People;
